import React, { useEffect, useState } from 'react';
import PhoneNumber from 'awesome-phonenumber';
import { MdChevronRight } from 'react-icons/all';
import { useHistory } from 'react-router';
import * as qs from 'qs';
import styled from 'styled-components';
import Checkbox from '@material-ui/core/Checkbox';
import Alert from '@mui/material/Alert';
import { CheckCircle, Cancel } from '@mui/icons-material';
import Button from '@material-ui/core/Button';
import { Trans } from 'react-i18next';
import toSessionIdParam from '../utils/sessionId';
import Header from '../components/TopHeader';
import { useSession } from '../contexts/SessionContext';
import BackgroundImage from '../components/BackgroundImage';
import { useVendingMachine } from '../contexts/VendingMachineContext';
import { getDetails, getMandates, requestMandate } from '../api/api';
import { useUser } from '../contexts/UserContext';
import { StyledTextField } from '../components/forms/FormStyles';
import i18n from '../i18n/i18n';
import { useTheme } from '../contexts/ThemeContext';
import MBWayLogo from '../images/mbwayLogo.png';
import GalpUserMenu from '../components/GalpUserMenu';

const StyledDiv = styled.div`
  margin: 2dvh;
`;

const Root = styled.div<{ $bgColor?: string }>`
    background-color: ${(props) => props.$bgColor};
    width: 100%;
    height: 100%;
`;

const StyledMain = styled.main<{ $chargeImageMarginTop?: string }>`
    display: flex;
    margin-top: ${(props) => props.$chargeImageMarginTop};
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 75%;
    gap: 2%;
`;

const Message = styled.span`
  text-align: center;
  font-size: 1.6em;
  color: rgba(77,76,77,255);
  margin-bottom: 20px;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const LogoContainer = styled.div`
  width: 100%;
  height: 5dvh;
  display: flex;
  justify-content: center;
  bottom: 0;
  position: fixed;
  padding: 10px;
  box-shadow: rgba(0, 0, 0, 0.6) 0 2px 5px;
  background-color: white;
`;

const StyledButton = styled(({ ...rest }) => (
  <Button
    {...rest}
  />
))`
  && {
    background-color: ${(props) => props.color};
    color: ${(props) => props.textColor};
    border-radius: 10px;
    width: auto;
    height: 45px;
    text-transform: capitalize;
    font-size: 0.95em;
    /* The back button lives in the header, so the button spans the screen's width */
    left: 15px;
    right: 15px;
    bottom: 10%;
    position: fixed;

    ${({ disabled }) => disabled && `
     background-color: rgb(243, 243, 243);
     color: rgba(255,255,255,0.15)
  `
}
`;

const StyledCheckbox = styled(({ ...rest }) => (
  <Checkbox
    {...rest}
  />
))`
  && {
    ${({ checked }) => checked && `
     color: green !important;
  `
}
`;

const Spacer = styled.div`
  margin-bottom: 60px;
`;

const HelexiaPhoneInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  color: #3f3f3f;
  font-family: Geologica, sans-serif;
  font-weight: 500;
  font-size: 14px;
  flex: 1;
  width: 100%;
  &::placeholder {
    color: #6a6a6a;
  }
`;

export default ({ location }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [mandate, setMandate] = useState<any>(undefined);
  const [acceptedMandate, setAcceptedMandate] = useState<boolean>(true);
  const phoneNumberFormatter = PhoneNumber.getAsYouType('PT');
  const telephoneNumber = new PhoneNumber(phoneNumber || '', 'PT');

  const { s } = qs.parse(location.search, { ignoreQueryPrefix: true });

  const { vendingMachineInfo } = useVendingMachine();
  const { selectPaymentMethod, sessionId } = useSession();
  const history = useHistory();
  const { user, loggedIn } = useUser();
  const { theme, themeName } = useTheme();
  const isHelexia = themeName === 'helexia';
  const isGalp = themeName === 'galp';

  const galpInitials = (() => {
    const authUser = (user as any)?.user;
    const source = authUser?.name || (authUser?.email || '').split('@')[0];
    const parts = source.split(/[\s._-]+/).filter(Boolean);
    const raw = parts.length >= 2 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : (parts[0]?.[0] || '');
    return raw.toUpperCase();
  })();

  const isEvCharger = vendingMachineInfo?.equipmentType?.label === 'EV Charger';

  const { paymentMethod } = history?.location?.state || {};

  const onSubmit = async (mobilePhone) => {
    if (!mandate && paymentMethod?.isMandateSupported && loggedIn) {
      const response = await requestMandate(
        vendingMachineInfo?.id,
        undefined,
        paymentMethod.gatewayName,
        telephoneNumber.getNumber().replace('+', ''),
      );

      if (response.state === 'PENDING') {
        history.push('/mandate', { mobilePhone, mandateId: response.socketId });
      }
    } else {
      await selectPaymentMethod({
        gatewayName: 'SPG',
        gatewayParameters: {
          mobilePhone,
        },
      });

      history.push(`/product-selection?sid=${toSessionIdParam(sessionId.current)}`);
    }
  };

  useEffect(() => {
    if (!vendingMachineInfo && !s) {
      history.push('/home-no-vm');
    } else if (!vendingMachineInfo && s) {
      history.push(`/load?s=${s}`);
    }

    (async () => {
      let userInfo;
      try {
        userInfo = await getDetails(user);
      } catch (e) {
        // ignore
      }
      phoneNumberFormatter.reset(userInfo?.mobilePhone);
      setPhoneNumber(phoneNumberFormatter.getPhoneNumber().getNumber('national'));
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (telephoneNumber.isMobile() && paymentMethod?.isMandateSupported && loggedIn) {
        const mandateDetails = await getMandates('SPG', vendingMachineInfo?.id);

        const findMandate = mandateDetails.filter((elem) => elem.alias.replace('351#', '') === phoneNumber
          .replaceAll(' ', ''));

        setMandate(findMandate[0]);
      }
    })();
  }, [phoneNumber]);

  const getAlert = (error: string) => (
    <Alert
      style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'start',
        width: '80%',
        marginTop: '20px',
      }}
      severity="error"
    >
      {i18n.t(error)}
    </Alert>
  );

  if (isGalp) {
    const canSubmit = telephoneNumber.isMobile() && (mandate ? mandate.isActive : acceptedMandate);

    return (
      <div style={{
        width: '100%',
        height: '100dvh',
        background: 'white',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        fontFamily: theme.bodyFont,
      }}
      >
        {/* Header */}
        <div style={{
          position: 'relative',
          background: '#f7f4ed',
          minHeight: '94px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 16px',
          borderBottomLeftRadius: '14px',
          borderBottomRightRadius: '14px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            aria-label="back"
            onClick={() => history.push(`/home${s ? `?s=${s}` : ''}`)}
            style={{
              position: 'absolute',
              left: '16px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 1L1 6L6 11"
                stroke="#0b0b0b"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <p style={{
            fontFamily: theme.titleFont,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '24px',
            color: '#0b0b0b',
            margin: 0,
            padding: '0 40px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
          >
            {vendingMachineInfo?.name || ''}
          </p>
          <GalpUserMenu initials={galpInitials} />
        </div>

        {/* Body */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '32px',
          padding: '24px 16px',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
        >
          {isEvCharger && (
            <div style={{ textAlign: 'center' }}>
              <p style={{
                fontFamily: theme.bodyFont,
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '22px',
                color: '#111110',
                margin: 0,
              }}
              >
                {i18n.t('charger_ready_title')}
              </p>
              <p style={{
                fontFamily: theme.bodyFont,
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '22px',
                color: '#58595b',
                margin: '12px 0 0 0',
              }}
              >
                {i18n.t('charger_ready_subtitle')}
              </p>
            </div>
          )}

          {isEvCharger ? (
            <img
              src="/companies/GALP/charger_illustration.svg"
              alt=""
              style={{ width: '150px', height: '150px', display: 'block' }}
            />
          ) : (
            <div style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: '#f7f4ed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            >
              <img src={MBWayLogo} alt="MB WAY" style={{ width: '100px', height: 'auto' }} />
            </div>
          )}

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            width: '100%',
            alignItems: 'center',
          }}
          >
            <p style={{
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              color: '#111110',
              textAlign: 'center',
              margin: 0,
              whiteSpace: 'pre-line',
            }}
            >
              {i18n.t('inform_phone_mbway')}
            </p>
            <div style={{
              border: '2px solid #bab9ba',
              borderRadius: '14px',
              height: '46px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              boxSizing: 'border-box',
              width: '100%',
            }}
            >
              <input
                type="tel"
                placeholder="999 999 999"
                value={phoneNumber}
                onChange={(e) => {
                  phoneNumberFormatter.reset(e.target.value);
                  setPhoneNumber(phoneNumberFormatter.getPhoneNumber().getNumber('national') || e.target.value);
                }}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  width: '100%',
                  fontFamily: theme.bodyFont,
                  fontSize: '16px',
                  color: '#0b0b0b',
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          borderTop: '1px solid #bababa',
          background: 'white',
          height: '78px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 32px',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
        >
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onSubmit(telephoneNumber.getNumber().replace('+', ''))}
            style={{
              height: '36px',
              width: '100%',
              borderRadius: '57px',
              background: canSubmit ? theme.buttonColor : '#df9680',
              border: 'none',
              color: theme.buttonTextColor || 'white',
              fontFamily: theme.bodyFont,
              fontSize: '14px',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              boxShadow: '0px 2px 0px rgba(0,0,0,0.04)',
            }}
          >
            {i18n.t('authorize_payment')}
          </button>
        </div>
      </div>
    );
  }

  if (isHelexia) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        background: '#fcfcfc',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        paddingBottom: '46px',
      }}
      >
        <Header
          showVm
          userLogo
          showBackButton
          backButtonColor={theme.buttonColor}
          onBack={() => history.push(`/home${s ? `?s=${s}` : ''}`)}
        />
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: '23px 24px 8px',
          gap: '20px',
          overflowY: 'auto',
          alignItems: 'center',
        }}
        >
          {isEvCharger && (
            <p style={{
              fontFamily: 'Geologica, sans-serif',
              fontWeight: 500,
              fontSize: '22px',
              lineHeight: '28px',
              color: '#58595b',
              textAlign: 'center',
              margin: 0,
              width: '100%',
            }}
            >
              <Trans i18nKey="charger_ready" components={{ br: <br />, b: <span style={{ fontWeight: 'inherit' }} /> }} />
            </p>
          )}
          <div style={{
            width: '168px',
            height: '168px',
            borderRadius: '50%',
            background: '#fff9e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          >
            <img src={MBWayLogo} alt="MB WAY" style={{ width: '120px', height: 'auto' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '29px', width: '100%' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'center',
              width: '100%',
            }}
            >
              <p style={{
                fontFamily: 'Geologica, sans-serif',
                fontWeight: 600,
                fontSize: '20px',
                color: '#014f75',
                textAlign: 'center',
                letterSpacing: '-0.6px',
                margin: 0,
              }}
              >
                {i18n.t('mbway_number')}
              </p>
              <p style={{
                fontFamily: 'Geologica, sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                color: '#014f75',
                textAlign: 'center',
                letterSpacing: '-0.98px',
                width: '188px',
                margin: 0,
              }}
              >
                {i18n.t('mobile_phone')}
              </p>
              <div style={{
                background: '#efefef',
                borderRadius: '12px',
                height: '36px',
                padding: '0 10px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                width: '100%',
                boxSizing: 'border-box',
              }}
              >
                <span style={{
                  color: '#3f3f3f',
                  fontFamily: 'Geologica, sans-serif',
                  fontWeight: 500,
                  fontSize: '14px',
                  flexShrink: 0,
                }}
                >
                  +351
                </span>
                <HelexiaPhoneInput
                  placeholder={i18n.t('mobile_phone_number') as string}
                  value={phoneNumber}
                  onChange={(e) => {
                    phoneNumberFormatter.reset(e.target.value);
                    setPhoneNumber(phoneNumberFormatter.getPhoneNumber().getNumber('national') || e.target.value);
                  }}
                />
              </div>
            </div>
            <button
              type="button"
              style={{
                background: telephoneNumber.isMobile() && (mandate ? mandate.isActive : acceptedMandate)
                  ? 'linear-gradient(90deg, #fbd802 0%, #f7c749 100%)'
                  : '#d0d0d0',
                borderRadius: '12px',
                height: '36px',
                border: 'none',
                cursor: telephoneNumber.isMobile() && (mandate ? mandate.isActive : acceptedMandate) ? 'pointer' : 'not-allowed',
                color: 'white',
                fontFamily: 'Geologica, sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                letterSpacing: '-0.42px',
                width: '100%',
              }}
              disabled={!(telephoneNumber.isMobile() && (mandate ? mandate.isActive : acceptedMandate))}
              onClick={() => onSubmit(telephoneNumber.getNumber().replace('+', ''))}
            >
              {i18n.t('authorize_payment')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Root $bgColor={theme.backgroundColor}>
      <div>
        <Header
          showVm
          userLogo
          showBackButton
          backButtonColor={theme.buttonColor}
          onBack={() => history.push(`/home${s ? `?s=${s}` : ''}`)}
        />
      </div>
      <div style={{ position: 'relative' }}>
        <BackgroundImage
          equipmentTypeId={vendingMachineInfo?.equipmentType?.id}
          position="center top 50dvh"
          bgColor={theme.backgroundColor}
          style={{
            height: '85dvh',
          }}
        >
          <StyledMain $chargeImageMarginTop={theme.chargeImageMarginTop}>
            {isEvCharger && (
              <>
                <img src={theme.carChargeImage} alt="Logo" style={{ width: '100%' }} />
                <Message>
                  <Trans i18nKey="charger_ready" components={{ br: <br />, b: <strong /> }} />
                </Message>
              </>
            )}

            <StyledDiv>
              <span>{i18n.t('mobile_phone')}</span>
              <StyledTextField
                fullWidth
                onChange={(e) => {
                  phoneNumberFormatter.reset(e.target.value);
                  setPhoneNumber(phoneNumberFormatter.getPhoneNumber().getNumber('national') || e.target.value);
                }}
                id="mobilePhone"
                value={phoneNumber}
                placeholder="999 999 999"
              />
              {
                paymentMethod?.isMandateSupported && loggedIn && (
                  <>
                    {mandate && mandate.isActive && (
                      <div style={{ marginTop: '20px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'start',
                            alignItems: 'start',
                            padding: '10px',
                          }}
                        >
                          <CheckCircle style={{ color: 'green' }} />
                          <div>
                            <b>{i18n.t('authorized')}</b>
                            <br />
                            <br />
                            <small>{i18n.t('can_change_settings_in_mbway')}</small>
                          </div>
                        </div>
                      </div>
                    )}
                    {mandate && !mandate.isActive && (
                      <div style={{ marginTop: '20px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'start',
                            alignItems: 'start',
                            padding: '10px',
                          }}
                        >
                          <Cancel style={{ color: 'red', marginRight: '10px' }} />
                          <div>
                            <b>{i18n.t('suspended')}</b>
                            <br />
                            <br />
                            <small>{i18n.t('can_change_settings_in_mbway')}</small>
                          </div>
                        </div>
                      </div>
                    )}
                    {!mandate && (
                      <div style={{ backgroundColor: '#f8f8f8', marginTop: '20px' }}>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'start',
                            alignItems: 'start',
                            padding: '10px',
                          }}
                        >
                          <StyledCheckbox
                            checked={acceptedMandate}
                            onChange={(event) => {
                              setAcceptedMandate(event.currentTarget.checked);
                            }}
                          />
                          <div>
                            <b>{i18n.t('accept_add_authorized_payments')}</b>
                            <br />
                            <br />
                            <small>{i18n.t('dont_need_to_validate_next_purchases')}</small>
                          </div>
                        </div>
                      </div>
                    )}
                    {!acceptedMandate && getAlert('need_accept_authorized_payments')}
                    {mandate && !mandate.isActive && getAlert('need_change_authorized_payments')}
                  </>
                )
              }
            </StyledDiv>
          </StyledMain>
          <Spacer />
          <StyledButton
            variant="outlined"
            color={theme.buttonColor}
            textColor={theme.buttonTextColor}
            type="button"
            disabled={!(telephoneNumber.isMobile() && (mandate ? mandate.isActive : acceptedMandate))}
            onClick={() => onSubmit(telephoneNumber.getNumber().replace('+', ''))}
            endIcon={<MdChevronRight style={{ fontSize: '35px' }} />}
          >
            {i18n.t('authorize_payment')}
          </StyledButton>
        </BackgroundImage>
      </div>
        {theme.logo && (
            <LogoContainer>
                <img
                    src={theme.logo}
                    alt="logo"
                />
            </LogoContainer>
        )}
    </Root>
  );
};
